<template>
    <pc-interact
        v-if="show"
        class="bar"
        is-draggable
        v-bind:draggable-options="draggableOptions"
        v-bind:auto-scroll="false"
        v-on:drag-start="dragStart"
        v-on:drag-move="dragMove"
        v-on:mousedown="onClick">
        <div
            class="scroller"
            v-bind:style="scrollerStyleObject" />
    </pc-interact>
</template>

<script>
    import { isIE } from "Util/util";
    import { responsiveManager } from "Responsive/manager";
    export default {
        data() {
            return {
                scrollOffset: 0,
                startX: 0,
                element: null,
                width: 0,
                scrollWidth: 0,
                clientWidth: 0,
                startOffset: 0,
                animationContext: null
            };
        },
        computed: {
            scrollerStyleObject() {
                return {
                    width: `${this.scrollerSize}px`,
                    left: `${this.scrollOffset}px`
                };
            },
            draggableOptions() {
                return {
                    lockAxis: "x"
                };
            },
            show() {
                return this.scrollWidth > this.clientWidth;
            },
            visibleProportion() {
                return this.clientWidth / this.scrollWidth;
            },
            scrollerSize() {
                return this.visibleProportion * this.width;
            },
            maxScrollerOffset() {
                return this.width - this.scrollerSize;
            },
            maxElementOffset() {
                return this.scrollWidth - this.clientWidth;
            }
        },
        async created() {
            responsiveManager.addResizeListener(this.calcSizes);
            window.addEventListener("wheel", this.wheelScroll);
            await this.$nextTick();
            this.calcSizes();
        },
        methods: {
            onClick(event) {
                if (event.pageX > this.scrollOffset + this.scrollerSize) {
                    this.animateScrollToPosDelta(400);
                }
                if (event.pageX < this.scrollOffset) {
                    this.animateScrollToPosDelta(-400);
                }
            },
            setScrollerOffsetFromElement() {
                this.scrollOffset = (this.maxScrollerOffset / this.maxElementOffset) * this.element.scrollLeft;
            },
            async calcSizes() {
                if (this.element instanceof HTMLElement) {
                    this.scrollWidth = this.element.scrollWidth;
                    this.clientWidth = this.element.clientWidth;
                    this.setScrollerOffsetFromElement();
                }

                await this.$nextTick();
                if (this.$el instanceof HTMLElement) {
                    // circa 2020, the default thickness for scroll bars in most browsers (IE, Chrome, Firefox) is 17px
                    // if you change this, also change the size in the style below
                    const thicknessOfScrollbar = 17;
                    this.width = this.$el.clientWidth - thicknessOfScrollbar * 2;
                }
            },
            setElement(element) {
                if (element === null) {
                    if (this.element instanceof HTMLElement) {
                        this.element.removeEventListener("scroll", this.setScrollerOffsetFromElement);
                    }

                    this.element = null;
                    this.scrollWidth = 0;
                    this.clientWidth = 0;
                    this.scrollOffset = 0;
                    return;
                }
                if (!(element instanceof HTMLElement)) {
                    return;
                }
                this.element = element;

                this.element.addEventListener("scroll", this.setScrollerOffsetFromElement);
                this.calcSizes();
            },
            dragStart(dragEvent) {
                this.startX = dragEvent.pageX;
                this.startOffset = this.scrollOffset;
            },
            dragMove(dragEvent) {
                this.setScroll(this.startOffset + dragEvent.pageX - this.startX);
            },
            /**
             * @param {WheelEvent} event
             */
            wheelScroll(event) {
                if (event.shiftKey) {
                    if (isIE()) {
                        try {
                            event.preventDefault();
                            event.stopPropagation();
                        } catch (e) {
                            //do nothing
                        }
                    }

                    const delta = event.deltaY > 0 ? 100 : -100;
                    this.animateScrollToPosDelta(delta);
                }
            },
            animateScrollToPosDelta(delta) {
                if (this.animationContext) {
                    this.animationContext.endPos = this.animationContext.endPos + delta;
                    this.animationContext.startTime = null;
                } else {
                    this.animationContext = { endPos: this.scrollOffset + delta };
                }
                window.requestAnimationFrame(this.animateScroll);
            },
            animateScroll(timestamp) {
                if (!this.animationContext) {
                    return;
                }
                if (!this.animationContext.startTime) {
                    this.animationContext.startTime = timestamp;
                    this.animationContext.startPos = this.scrollOffset;
                }
                const deltaTime = timestamp - this.animationContext.startTime;
                // use linear interpolation to  smoothly change scroll position
                // scale time to 0-1 range. First calculate as a proportion of total duration(.2s), then clamp to range
                const t = Math.max(0, Math.min(deltaTime / 200, 1));
                const pos = (1 - t) * this.animationContext.startPos + t * this.animationContext.endPos;
                this.setScroll(pos);
                if (t < 1) {
                    window.requestAnimationFrame(this.animateScroll);
                } else {
                    this.animationContext = null;
                }
            },
            setScroll(scrollPos) {
                this.scrollOffset = Math.max(0, Math.min(scrollPos, this.maxScrollerOffset));
                if (this.element) {
                    const elementOffset = (this.maxElementOffset / this.maxScrollerOffset) * this.scrollOffset;
                    this.element.scrollLeft = elementOffset;
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    // circa 2020, the default thickness for scroll bars in most browsers (IE, Chrome, Firefox) is 17px
    // if you change this, also change the size in the thicknessOfScrollbar local const above
    $thicknessOfScrollbar: 17px;

    .bar {
        height: $thicknessOfScrollbar;
        width: 100%;
        position: fixed;
        bottom: 0;
        left: 0;
        padding: 1px $thicknessOfScrollbar;
        background-color: #f1f1f1;
        z-index: 100000;
        &::before,
        &::after {
            width: $thicknessOfScrollbar;
            height: $thicknessOfScrollbar;
            position: absolute;
            top: 0;
            display: block;
            text-align: center;
            font-size: $thicknessOfScrollbar;
            line-height: $thicknessOfScrollbar;
        }
        &::before {
            left: 0;
            content: "\25C2"; //unicode for small left triangle
        }
        &::after {
            right: 0;
            content: "\25B8"; //unicode for small right triangle
        }
        .scroller {
            height: 100%;
            position: relative;
            //colors taken from the ones that chrome uses
            background-color: #c1c1c1;
            &:hover {
                background-color: #a8a8a8;
            }
        }
    }
</style>
